import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Blockquote, Button, ButtonLink, Card } from '@ui';
import { bookModuleHref, useSelfServiceBooking } from 'components/Cashier/billing';
import { useClubSlug } from 'components/Clubs/hooks';
import Loader from 'components/Loader';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useSubscriptionStatus, { SubscriptionStatusData } from '~/hooks/useSubscriptionStatus';

type Props = {
    clubSlug?: string;
    permission: string;
    children?: React.ReactNode;
};

/** Guards content behind a subscription plan check. Empty permission is a pass-through. */
export const CheckSubscription: React.FC<React.PropsWithChildren<Props>> = ({ clubSlug, permission, children }) => {
    const { t } = useTranslation('general');
    const { isLoading, isAuthenticated, permissionError, data, error } = useSubscriptionStatus({
        clubSlug,
        permission
    });

    return (
        <Loader isLoading={isLoading && clubSlug !== undefined}>
            {/* Only show error when there is no cached data — a transient revalidation error
                should not unmount children while stale data is still available. */}
            {error && !data ? (
                <div className="tw-flex tw-h-full tw-flex-auto tw-items-center tw-justify-center tw-px-4 tw-py-8">
                    <div className="tw-w-full lg:tw-w-2/3 xl:tw-w-1/2">
                        <Blockquote title={t('permissions.checkSubscription.loadErrorTitle')} color="error">
                            <p className="tw-mb-0">{t('permissions.checkSubscription.loadErrorText')}</p>
                        </Blockquote>
                    </div>
                </div>
            ) : isAuthenticated && !permissionError ? (
                children
            ) : (
                <div className="tw-flex tw-h-full tw-flex-auto tw-items-center tw-justify-center tw-px-4 tw-py-8">
                    <div className="tw-w-full lg:tw-w-2/3 xl:tw-w-1/2">
                        <AccessDeniedInfo clubSlug={clubSlug} permission={permission} statusData={data} />
                    </div>
                </div>
            )}
        </Loader>
    );
};

interface AccessDeniedInfoProps {
    clubSlug?: string;
    permission: string;
    statusData?: SubscriptionStatusData;
}

/** Card shown when the club has no active subscription for the required plan. */
const AccessDeniedInfo = ({ clubSlug, permission, statusData }: AccessDeniedInfoProps) => {
    const { t } = useTranslation('general');
    const canBook =
        useSelfServiceBooking(useClubSlug(clubSlug)) && Boolean(permission) && statusData?.can_book === true;

    return (
        <div className="tw-space-y-4">
            <Blockquote title={t('permissions.checkSubscription.notIncludedTitle')} color="error">
                <p className="tw-mb-0">{t('permissions.checkSubscription.notIncludedText')}</p>
            </Blockquote>

            {canBook ? (
                <Card flat>
                    <h3 className="tw-mb-2 tw-text-xl tw-font-semibold">{t('permissions.checkSubscription.bookTitle')}</h3>
                    <p className="tw-mb-4 tw-text-sm tw-text-accents-9">{t('permissions.checkSubscription.bookText')}</p>
                    <ButtonLink href={bookModuleHref(permission)} block size="md">
                        {t('permissions.checkSubscription.bookNow')}
                    </ButtonLink>
                </Card>
            ) : (
                <Card flat>
                    <h3 className="tw-mb-2 tw-text-xl tw-font-semibold">
                        {t('permissions.checkSubscription.upgradeTitle')}
                    </h3>
                    <p className="tw-mb-4 tw-text-sm tw-text-accents-9">
                        {t('permissions.checkSubscription.upgradeText')}
                    </p>
                    <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
                        <Button.Link href="mailto:info@sportision.de">
                            <FontAwesomeIcon icon="envelope" />
                            {t('permissions.checkSubscription.viaMail')}
                        </Button.Link>
                        <Button.Link color="light" href="tel:029228751440">
                            <FontAwesomeIcon icon="phone-alt" />
                            {t('permissions.checkSubscription.viaPhone')}
                        </Button.Link>
                    </div>
                </Card>
            )}
        </div>
    );
};
